<template>
    <div class="page-wrapper">

        <project-header
            v-if="projectFetched"
            :project="project"
        />

        <form
            autocomplete="off"
            novalidate
            style="max-width: 500px"
        >
            <q-field>
                <h-input
                    :value="project.name"
                    @change="val => { project.name = val; saveProject() }"
                    show-save-buttons
                    stack-label="Название"
                />
            </q-field>
            <q-field>
                <q-input
                    stack-label="ID (UUID)"
                    v-model="project.id"
                    :disable="true"
                />
            </q-field>
            <q-field>
                <q-input
                    stack-label="Сервис код"
                    v-model="project.service_code"
                    :disable="true"
                />
            </q-field>
            <q-field>
                <q-input
                    stack-label="Регион"
                    v-model="project.region"
                    :disable="true"
                />
            </q-field>
            <q-field>
                <q-input
                    stack-label="Дата создания"
                    v-model="project.created_at"
                    :disable="true"
                />
            </q-field>
            <q-field>
                <q-input
                    stack-label="Дата обновления"
                    v-model="project.updated_at"
                    :disable="true"
                />
            </q-field>
            <q-field>
                <q-select
                    v-model="project.prod_location"
                    :options="locations"
                    @change="saveProject"
                    stack-label="Размещение"
                />
            </q-field>
            <q-field>
                <q-select
                    v-model="project.has_custom"
                    :options="[{label: 'Да', value: true}, {label: 'Нет', value: false}]"
                    @change="saveProject"
                    stack-label="Кастом"
                />
            </q-field>
            <q-field>
                <q-select
                    v-model="project.status"
                    :options="statusesOptions"
                    @change="saveStatus"
                    stack-label="Статус"
                />
            </q-field>
            <q-field>
                <q-select
                    v-model="project.manager.id"
                    :options="managersOptions"
                    @change="saveManager"
                    stack-label="Менеджер"
                />
            </q-field>
            <q-field>
                <q-select
                    v-model="project.service_provider_id"
                    :options="serviceProvidersOptions"
                    @change="saveProject"
                    stack-label="Поставщик услуг"
                />
            </q-field>
        </form>

        <h2 id="client">Клиент</h2>
        <project-owner/>

        <h2 id="promo-code">Промокод</h2>
        <project-promo-code/>

        <h2 id="managers">Менеджеры проекта</h2>
        <project-users-list/>

        <h2>Блокировка при DDoS-атаке</h2>
        <project-ssl
            v-if="projectFetched"
            :project="project"
            @statusChange="saveProject"
        />

        <h2 style="display: flex; align-items: center;">
            Домены
            <q-btn
                @click="syncMcpStatus"
                icon="cloud_download"
                class="hover-primary q-ml-sm"
                :loading="syncingMcpStatus"
                flat
                round
            >
                <q-tooltip
                    :delay="500"
                    :offset="[0, 5]"
                >
                    Получить данные с MCP
                </q-tooltip>
            </q-btn>
        </h2>
        <project-domains/>

        <h2>Логи</h2>
        <project-logs/>

        <h2>Теги</h2>
        <q-chip
            v-for="(tag, index) in project.tags"
            @hide="detachTag(tag)"
            :key="index"
            closable
            square
            color="secondary"
            style="margin-right: 10px;"
        >
            {{ tag }}
        </q-chip>
        <div class="q-mt-md">
            <tags-picker
                v-model="tagsPickerOpened"
                :attachedTags="project.tags"
                @save="(addedTags, removedTags) => { updateTags(addedTags, removedTags) }"/>
            <q-btn
                @click="tagsPickerOpened = true"
                label="Добавить"
            />
        </div>

        <h2>Комментарии</h2>
        <div v-for="comment in comments" :key="comment.id" class="comment-item">
            <div class="comment-body">
                <div class="comment-info">
                    <span
                        class="comment-author">{{
                            comment.commentator
                                ? `${comment.commentator.first_name} ${comment.commentator.last_name}`
                                : '(Менеджер уделен)'
                        }}</span>
                    <span class="comment-date">{{ formatDate(comment.updated_at) }}</span>
                </div>
                <div class="comment-text" v-html="comment.message"></div>
                <div class="comment-controls">
                    <q-btn flat round dense color="primary" icon="edit" @click="openCommentModal(comment)"/>
                    <q-btn flat round dense color="negative" icon="delete" @click="deleteComment(comment)"/>
                </div>
            </div>
        </div>
        <q-modal v-model="commentModalOpened" no-backdrop-dismiss :content-css="{width: '810px', padding: '24px'}">
            <h3>Редактировать комментарий</h3>
            <q-editor
                v-model="editedComment.message"
                :toolbar="editorToolbar"
                min-height="100px"
            />
            <div class="q-mt-lg" style="text-align: right;">
                <q-btn color="primary" @click="updateComment" label="Сохранить"/>
                <q-btn color="primary" flat @click="closeCommentModal" label="Отмена"/>
            </div>
        </q-modal>
        <div class="new-comment">
            <q-btn @click="editorIsVisible=true" v-if="!editorIsVisible" icon="comment" label="Комментарий"/>
            <div v-if="editorIsVisible">
                <q-editor
                    v-model="newComment"
                    :toolbar="editorToolbar"
                    min-height="100px"
                />
                <div class="q-mt-md">
                    <q-btn color="primary" @click="saveComment" label="Сохранить"/>
                    <q-btn color="primary" flat @click="cancelNewComment" label="Отмена"/>
                </div>
            </div>
        </div>
        <div
            v-if="$auth.check('root')"
            class="q-mt-xl flex justify-end"
        >
            <q-btn
                @click="deleteProject()"
                icon="delete"
                color="negative"
                label="Удалить проект"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import _ from 'lodash'

import columnsValues from '@/HTable/columnsValues'

import { BarChart, HTable, HTh, ProjectDomains, ProjectLogs, ProjectOwner, ProjectPromoCode, ProjectSsl, TagsPicker } from '@'

import HInput from '@/HInput'

import {
    date,
    QBtn,
    QCard,
    QCardMain,
    QCardTitle,
    QChip,
    QEditor,
    QField,
    QIcon,
    QInput,
    QItem,
    QItemMain,
    QItemSeparator,
    QItemSide,
    QModal,
    QSelect,
    QTable,
    QTableColumns,
    QTd,
    QTh,
    QToggle,
    QTooltip,
    QTr,
} from 'quasar'

import ProjectHeader from '../components/ProjectHeader/ProjectHeader.vue'
import ProjectUsersList from '../components/ProjectUsersList/ProjectUsersList'

export default {
    components: {
        ProjectUsersList,
        ProjectHeader,
        QField,
        QInput,
        QSelect,
        QChip,
        QBtn,
        QToggle,
        QTooltip,
        QIcon,
        QModal,
        QCard,
        QCardTitle,
        QCardMain,
        QItem,
        QItemSide,
        QItemMain,
        QItemSeparator,
        QTable,
        QTableColumns,
        QTr,
        QTh,
        QTd,
        QEditor,
        date,
        HTable,
        HTh,
        HInput,
        BarChart,
        TagsPicker,
        ProjectDomains,
        ProjectLogs,
        ProjectOwner,
        ProjectPromoCode,
        ProjectSsl,
    },
    data () {
        return {
            comments: [],
            newComment: '',
            editedComment: {
                id: null,
                message: '',
            },
            editorIsVisible: false,
            editorToolbar: [
                [
                    {
                        label: 'Style',
                        icon: this.$q.icon.editor.formatting,
                        list: 'no-icons',
                        options: ['h1', 'h2', 'p', 'code'],
                    },
                    'removeFormat',
                    'quote',
                    'link',
                ],
                ['bold', 'italic', 'underline', 'strike'],
                ['unordered', 'ordered'],
            ],
            commentModalOpened: false,
            managersOptions: [],
            statusesOptions: [],
            tags: [],
            tagsPickerOpened: false,
            serviceProvidersOptions: [],
            syncingMcpStatus: false,
            newDomain: '',
            loading: false,
            edit: {
                enabled: false,
                modelName: '',
                modelData: '',
            },
            tempFieldData: '',
            newUser: false,
        }
    },
    validations: {
        project: {},
    },
    mounted () {
        this.fetchTags()
        this.fetchComments()
        this.fetchManagers()
        this.fetchActiveStatus()
        this.fetchServiceProviders()
        this.setLocationHref()
    },
    computed: {
        ...mapState({
            project (state) {
                let project = state.project.data,
                    manager = state.project.data.manager

                return {
                    name: project.name || '',
                    id: project.id || null,
                    service_code: project.service_code || null,
                    created_at: project.created_at || null,
                    updated_at: project.updated_at || null,
                    prod_location: project.prod_location || 'NONE',
                    has_custom: project.has_custom || false,
                    status: project.status || '',
                    domains: project.domains || [],
                    tags: project.tags || [],
                    ssl: project.ssl || false,
                    ssl_deployment_status: project.ssl_deployment_status || false,
                    service_provider_id: project.service_provider_id || null,
                    manager: {
                        id: manager && manager.id || null,
                    },
                    region: project.region || 'UA',
                    is_blocked: project.is_blocked || false,
                }
            },
        }),
        ...mapGetters({
            projectFetched: 'project/fetched',
        }),
        locations () {
            return columnsValues.locations
        },
    },
    methods: {
        fetchTags () {
            this.$http.get(`api/v1.0/projects/${this.$route.params.id}/tags`).then((response) => {
                this.tags = response.data
            })
        },
        fetchComments () {
            this.$http.get(`api/v1.0/projects/${this.$route.params.id}/comments`).then((response) => {
                this.comments = response.data
            })
        },
        fetchManagers () {
            this.$http.get(`api/v1.0/users/?filter[role]=manager&perPage=60`).then(response => {
                this.managers = response.data.users

                this.managersOptions = response.data.users.map(
                    user => {
                        return {
                            label: `${user.first_name} ${user.last_name}`,
                            value: user.id,
                        }
                    },
                )
            })
        },
        fetchStatuses () {
            this.$http.get(`api/v1.0/statuses`).then((response) => {
                this.statuses = response.data
                _.forOwn(response.data, (value, key) => {
                    this.statusesOptions.push({
                        label: value,
                        value: key,
                    })
                })
            })
        },
        fetchActiveStatus () {
            this.$http.get(`api/v1.0/projects/${this.$route.params.id}/active_status`).then((response) => {
                this.statuses = response.data
                _.forOwn(response.data, (value, key) => {
                    this.statusesOptions.push({
                        label: value,
                        value: key,
                    })
                })
            })
        },
        fetchServiceProviders () {
            this.$http.get(`/api/v1.0/service-providers`).then(response => {
                this.$store.commit('serviceProviders/setData', response.data)
                _.forEach(response.data, (value, key) => {
                    this.serviceProvidersOptions.push({
                        label: value.alias,
                        value: value.id,
                    })
                })
            })
        },
        updateTags (addedTags, removedTags) {
            if (removedTags.length) {
                this.project.tags = this.project.tags.filter(
                    tagName => !removedTags.map(removedTag => removedTag.name).includes(tagName),
                )
                this.tags = this.tags.filter(
                    tagObj => !removedTags.map(removedTag => removedTag.name).includes(tagObj.name),
                )
            }

            if (addedTags.length) {
                addedTags.forEach(addedTag => {
                    if (!this.tags.find(tagObj => tagObj.name === addedTag.name)) {
                        this.project.tags = this.project.tags.concat(addedTag.name)
                        this.tags = this.tags.concat(addedTag)
                    }
                })
            }

            this.$store.commit('project/setProject', this.project)
            this.$http.post(`api/v1.0/projects/${this.$route.params.id}/tags/sync`, this.tags.map(tag => tag.id)).
                then(response => {
                    this.$q.notify({
                        message: `Теги обновлены`,
                        type: 'positive',
                        position: 'bottom',
                    })
                })
        },
        detachTag (name) {
            this.$q.dialog({
                title: `Удаление тега`,
                message: `${name}`,
                ok: true,
                cancel: true,
            }).then(() => {
                let tagId = this.tags.find(
                    value => value.name === name,
                ).id

                this.project.tags = this.project.tags.filter(tagName => tagName !== name)
                this.$store.commit('project/setProject', this.project)

                this.$http.post(`api/v1.0/projects/${this.$route.params.id}/tags/detach`, [tagId]).then(response => {
                    this.$q.notify({
                        message: `Тег удален`,
                        type: 'info',
                        position: 'bottom',
                    })
                })
            }).catch(() => {
            })
        },
        saveProject () {
            this.$store.commit('project/setProject', this.project)
            this.$http.put(`api/v1.0/projects/${this.$route.params.id}`, this.project).then((response) => {
                this.$q.notify({
                    message: `Сохранено`,
                    type: 'positive',
                    position: 'bottom',
                })
            }).catch((error) => {
                let response = error.response,
                    data = response.data

                if (response.status === 403 && data.errors) {
                    this.$q.notify({ message: data.errors[0], type: 'negative' })
                } else {
                    _.forIn(error.response.data.errors, (value, key) => {
                        this.$q.notify(value[0])
                    })
                }
            })
        },
        discard () {
            _.set(this, this.edit.modelName, this.edit.modelData)
            this.edit = Object.assign({}, {
                enabled: false,
                modelName: '',
                modelData: null,
            })
        },
        saveComment () {
            this.$http.post(`api/v1.0/projects/${this.$route.params.id}/comments`, { 'message': this.newComment }).
                then(response => {
                    this.comments.push(response.data)
                    this.editorIsVisible = false
                    this.newComment = ''
                })
        },
        updateComment () {
            this.$http.put(`api/v1.0/projects/${this.$route.params.id}/comments/${this.editedComment.id}`,
                { 'message': this.editedComment.message }).then(response => {
                this.comments.find(
                    value => value.id === this.editedComment.id,
                ).message = this.editedComment.message
                this.commentModalOpened = false
            })
        },
        deleteComment (comment) {
            if (confirm('Вы уверены, что хотите удалить этот комментарий?')) {
                this.$http.delete(`api/v1.0/projects/${this.$route.params.id}/comments/${comment.id}`).
                    then(response => {
                        this.comments.splice(this.comments.indexOf(comment), 1)
                    })
            }
        },
        openCommentModal (comment) {
            Object.assign(this.editedComment, comment)
            this.commentModalOpened = true
        },
        cancelNewComment () {
            if (this.newComment !== '' &&
                !confirm('Если вы закроете окно без сохранения, комментарий будет потерян.')) {
                return
            }
            this.newComment = ''
            this.editorIsVisible = false
        },
        closeCommentModal () {
            if (this.editedComment.message !==
                this.comments.find(value => value.id === this.editedComment.id).message &&
                !confirm('Если вы закроете окно без сохранения, изменения будет потеряны.')) {
                return
            }
            this.commentModalOpened = false
        },
        saveManager () {
            let manager = this.managers.find(value => value.id === this.project.manager.id)

            this.$store.commit('project/setProjectManager', manager)
            this.$http.post(`api/v1.0/projects/${this.$route.params.id}/managers/assign`,
                { 'manager_id': this.project.manager.id }).then(response => {
                this.$q.notify({ message: 'Сохранено', type: 'positive' })
            })
        },
        saveStatus () {
            this.$store.commit('project/setProjectStatus', this.project.status)
            this.$http.post(`api/v1.0/projects/${this.$route.params.id}/active_status`,
                { 'status': this.project.status }).
                then(response => {
                    this.$q.notify({ message: 'Сохранено', type: 'positive' })
                })
        },
        editField (name) {
            this.edit = {
                enabled: true,
                modelName: name,
                modelData: _.get(this, name),
            }
        },
        formatDate (rowDate) {
            return date.formatDate(rowDate, 'YYYY-MM-DD')
        },
        setLocationHref () {
            const anchor = this.$router.currentRoute.hash
            this.$nextTick(() => {
                if (anchor && document.querySelector(anchor)) {
                    location.href = anchor
                }
            })
        },
        deleteProject () {
            this.$q.dialog(
                {
                    title: `Удалить проект ${this.project.name}?`,
                    message: `Данные проекта будут уничтожены`,
                    ok: true,
                    cancel: true,
                },
            ).then(() => {
                this.$http.delete(`api/v1.0/projects/${this.project.id}`).then(() => {
                    this.$q.dialog(
                        {
                            title: `Проект ${this.project.name} поставлен в очередь на удаление`,
                            ok: true,
                        },
                    ).then(() => {
                        this.$router.push('/projects')
                    })
                }).catch((error) => {
                    this.$q.notify({
                        message: error.message,
                        type: 'negative',
                        position: 'bottom',
                    })
                })
            }).catch(() => {
            })
        },
        syncMcpStatus () {
            this.syncingMcpStatus = true

            this.$store.dispatch('project/syncMcpStatus', this.project).then((data) => {
                this.$q.notify({
                    message: data.message || `Данные из MCP успешно обновлены`,
                    type: 'positive',
                    position: 'bottom',
                })
                
                this.$store.commit('project/setProjectBlocked', data.blocked);
            }).catch(e => {
                this.$q.notify({
                    message: `${e.message}`,
                    type: 'negative',
                    position: 'bottom',
                })
            }).finally(() => {
                this.syncingMcpStatus = false
            })
        },
    },
}
</script>

<style scoped lang="scss">
.comment-info {
    font-size: 13px;
    margin-bottom: 10px;
}

.comment-author {
    font-weight: 500;
}

.comment-date {
    color: rgb(66, 82, 110);
}

.comment-item {
    position: relative;
    padding: 15px 0;
    border-bottom: 1px solid #ccc;

    &:hover {
        .comment-controls {
            display: block;
        }
    }
}

.comment-controls {
    display: none;
    position: absolute;
    top: 0;
    right: 0;
}

.new-comment {
    margin-top: 20px;
}

.manager {
    position: relative;
    margin: 10px 0;
    min-height: 32px;
    outline: 0;
    padding: 2px 0 8px;

    &:before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        background: currentColor;
        height: 1px;
        color: #bdbdbd;
        -webkit-transform: scaleY(1);
        transform: scaleY(1);
    }
}
</style>




// WEBPACK FOOTER //
// resources/assets/js/views/ProjectDetailsView.vue